// src/store/crm/churn/index.js
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import toast from "react-hot-toast";
import moment from "moment";
import { churnApi } from "./churnApi";

const listParams = (filters) => ({
  segment: filters.segment === "all" ? undefined : filters.segment,
  customer_type:
    filters.customer_type === "AllCustomer" ? undefined : filters.customer_type,
  staff_id: filters.staff_id || undefined,
  search: filters.search || undefined,
  date_from: filters.date_from,
  date_to: filters.date_to,
  page: filters.page,
  limit: filters.limit,
  order_by: filters.order_by,
});

export const getChurnOverview = createAsyncThunk(
  "churn/getOverview",
  async (_, { getState }) => {
    try {
      return await churnApi.getOverview(listParams(getState().churnStore.filters));
    } catch (error) {
      toast.error(error?.message || "Failed to load churn overview");
      return null;
    }
  }
);

export const getChurnCustomers = createAsyncThunk(
  "churn/getCustomers",
  async (_, { getState }) => {
    try {
      return await churnApi.getCustomers(listParams(getState().churnStore.filters));
    } catch (error) {
      toast.error(error?.message || "Failed to load customers");
      return null;
    }
  }
);

export const getChurnSaves = createAsyncThunk(
  "churn/getSaves",
  async (_, { getState }) => {
    try {
      return await churnApi.getSaves(listParams(getState().churnStore.filters));
    } catch (error) {
      toast.error(error?.message || "Failed to load saves");
      return null;
    }
  }
);

// Accepts a plain user id, or `{ userId, silent }` where `silent` swallows the
// error toast. Silent is for screens where insights are a side panel next to
// something else (Conversations): a chat contact who isn't a churn-eligible
// customer makes this endpoint 422, and the sidebar already renders its own
// "not available" state, so a red toast on every chat open is just noise.
export const getCustomerInsights = createAsyncThunk(
  "churn/getInsights",
  async (arg) => {
    const { userId, silent } =
      arg && typeof arg === "object" ? arg : { userId: arg, silent: false };
    try {
      const data = await churnApi.getInsights(userId);
      return { userId, data };
    } catch (error) {
      if (!silent) toast.error(error?.message || "Failed to load insights");
      return { userId, data: null };
    }
  }
);

export const getContactLog = createAsyncThunk("churn/getContactLog", async (customerId) => {
  try {
    const data = await churnApi.getContactLog({ customer_id: customerId, page: 1, limit: 20 });
    return { customerId, data };
  } catch (error) {
    toast.error(error?.message || "Failed to load contact history");
    return { customerId, data: null };
  }
});

export const storeContactLog = createAsyncThunk(
  "churn/storeContactLog",
  async (payload) => {
    try {
      const row = await churnApi.storeContactLog(payload);
      toast.success("Contact logged");
      return row;
    } catch (error) {
      toast.error(error?.message || "Failed to log contact");
      return null;
    }
  }
);

export const defaultChurnFilters = () => ({
  segment: "all",
  customer_type: "AllCustomer",
  staff_id: null,
  search: "",
  date_from: moment().subtract(30, "days").format("YYYY-MM-DD"),
  date_to: moment().format("YYYY-MM-DD"),
  page: 1,
  limit: 10,
  order_by: "most_overdue",
});

export const churnSlice = createSlice({
  name: "churnStore",
  initialState: {
    overview: null,
    customers: null, // Laravel paginator shape
    saves: null,
    insights: {}, // keyed by user_id
    contactLog: {}, // keyed by customer_id
    filters: defaultChurnFilters(),
    loading: { overview: false, customers: false, saves: false, insights: false, contactLog: false },
    latestRequest: { overview: null, customers: null, saves: null, insights: null, contactLog: null },
  },
  reducers: {
    // Any filter change except explicit paging resets to page 1.
    updateChurnFilters: (state, action) => {
      const next = { ...state.filters, ...action.payload };
      if (action.payload.page === undefined) next.page = 1;
      state.filters = next;
    },
    resetChurnFilters: (state) => {
      state.filters = defaultChurnFilters();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getChurnOverview.pending, (state, action) => {
        state.loading.overview = true;
        state.latestRequest.overview = action.meta.requestId;
      })
      .addCase(getChurnOverview.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.latestRequest.overview) return;
        state.loading.overview = false;
        if (action.payload) state.overview = action.payload;
      })
      .addCase(getChurnCustomers.pending, (state, action) => {
        state.loading.customers = true;
        state.latestRequest.customers = action.meta.requestId;
      })
      .addCase(getChurnCustomers.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.latestRequest.customers) return;
        state.loading.customers = false;
        if (action.payload) state.customers = action.payload;
      })
      .addCase(getChurnSaves.pending, (state, action) => {
        state.loading.saves = true;
        state.latestRequest.saves = action.meta.requestId;
      })
      .addCase(getChurnSaves.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.latestRequest.saves) return;
        state.loading.saves = false;
        if (action.payload) state.saves = action.payload;
      })
      .addCase(getCustomerInsights.pending, (state, action) => {
        state.loading.insights = true;
        state.latestRequest.insights = action.meta.requestId;
      })
      .addCase(getCustomerInsights.fulfilled, (state, action) => {
        if (action.meta.requestId === state.latestRequest.insights) state.loading.insights = false;
        if (action.payload.data) state.insights[action.payload.userId] = action.payload.data;
      })
      .addCase(getContactLog.pending, (state, action) => {
        state.loading.contactLog = true;
        state.latestRequest.contactLog = action.meta.requestId;
      })
      .addCase(getContactLog.fulfilled, (state, action) => {
        if (action.meta.requestId === state.latestRequest.contactLog) state.loading.contactLog = false;
        if (action.payload.data) state.contactLog[action.payload.customerId] = action.payload.data;
      })
      .addCase(storeContactLog.fulfilled, (state, action) => {
        const row = action.payload;
        if (row && state.contactLog[row.customer_id]?.data) {
          state.contactLog[row.customer_id].data.unshift(row);
        }
      })
      .addCase(getChurnOverview.rejected, (state, action) => {
        if (action.meta.requestId !== state.latestRequest.overview) return;
        state.loading.overview = false;
      })
      .addCase(getChurnCustomers.rejected, (state, action) => {
        if (action.meta.requestId !== state.latestRequest.customers) return;
        state.loading.customers = false;
      })
      .addCase(getChurnSaves.rejected, (state, action) => {
        if (action.meta.requestId !== state.latestRequest.saves) return;
        state.loading.saves = false;
      })
      .addCase(getCustomerInsights.rejected, (state, action) => {
        if (action.meta.requestId === state.latestRequest.insights) state.loading.insights = false;
      })
      .addCase(getContactLog.rejected, (state, action) => {
        if (action.meta.requestId === state.latestRequest.contactLog) state.loading.contactLog = false;
      });
  },
});

export const { updateChurnFilters, resetChurnFilters } = churnSlice.actions;
export default churnSlice.reducer;
